import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import {
  FileText,
  Clock,
  CheckCircle2,
  AlertCircle,
  ChevronRight,
  CalendarDays,
  Brain,
  Receipt
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const stageLabels = {
  new: "New",
  fact_finding: "Fact Finding",
  compliance: "Compliance",
  lender_submission: "Lender Submission",
  survey: "Survey",
  offer: "Offer",
  solicitors: "Solicitors",
  legal_completion: "Legal Completion",
  completed: "Completed"
};

const stageProgress = {
  new: 10,
  fact_finding: 20,
  compliance: 35,
  lender_submission: 50,
  survey: 60,
  offer: 75,
  solicitors: 85,
  legal_completion: 95,
  completed: 100
};

export default function ClientDashboard() {
  const { user, getAuthHeaders } = useAuth();
  const [applications, setApplications] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [appsRes, statsRes] = await Promise.all([
        axios.get(`${API}/applications?client_id=${user.id}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/dashboard/stats`, { headers: getAuthHeaders() })
      ]);
      setApplications(appsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
    setLoading(false);
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8" data-testid="client-dashboard">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl lg:text-3xl font-bold mb-2">Welcome back, {user?.name}</h1>
          <p className="text-slate-400">Track your mortgage applications and manage your documents.</p>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Link to="/applications">
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Card className="bg-gradient-to-br from-blue-600/20 to-blue-800/20 border-blue-500/30 hover:border-blue-500/50 cursor-pointer">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                    <FileText className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Applications</p>
                    <p className="text-xl font-bold font-mono">{applications.length}</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </Link>

          <Link to="/fact-finder">
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Card className="bg-gradient-to-br from-amber-600/20 to-amber-800/20 border-amber-500/30 hover:border-amber-500/50 cursor-pointer">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                    <CalendarDays className="w-5 h-5 text-amber-500" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Fact Finder</p>
                    <p className="text-xl font-bold">Start</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </Link>

          <Link to="/ai-analysis">
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Card className="bg-gradient-to-br from-purple-600/20 to-purple-800/20 border-purple-500/30 hover:border-purple-500/50 cursor-pointer">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                    <Brain className="w-5 h-5 text-purple-500" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">AI Analysis</p>
                    <p className="text-xl font-bold">Analyze</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </Link>

          <Link to="/invoices">
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Card className="bg-gradient-to-br from-green-600/20 to-green-800/20 border-green-500/30 hover:border-green-500/50 cursor-pointer">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                    <Receipt className="w-5 h-5 text-green-500" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Invoices</p>
                    <p className="text-xl font-bold font-mono">{stats?.pending_invoices || 0}</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </Link>
        </div>

        {/* Active Applications */}
        <Card className="bg-[#0f172a] border-slate-800 mb-8">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Your Applications</CardTitle>
            <Link to="/applications">
              <Button variant="ghost" size="sm" className="text-blue-500">
                View All <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-4">
                {[1, 2].map(i => (
                  <div key={i} className="h-24 bg-slate-800 animate-pulse rounded-lg"></div>
                ))}
              </div>
            ) : applications.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400 mb-4">No applications yet</p>
                <Link to="/book-appointment">
                  <Button className="bg-blue-600 hover:bg-blue-700">
                    Book Consultation
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {applications.slice(0, 3).map((app) => (
                  <motion.div
                    key={app.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-[#020617] rounded-lg p-4 border border-slate-800"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="font-semibold">{app.mortgage_type}</p>
                        <p className="text-sm text-slate-400">
                          £{app.loan_amount?.toLocaleString()} loan
                        </p>
                      </div>
                      <Badge className={`stage-${app.stage} text-white`}>
                        {stageLabels[app.stage]}
                      </Badge>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-xs text-slate-400">
                        <span>Progress</span>
                        <span>{stageProgress[app.stage]}%</span>
                      </div>
                      <Progress value={stageProgress[app.stage]} className="h-2" />
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Documents Needed */}
        <Card className="bg-[#0f172a] border-slate-800">
          <CardHeader>
            <CardTitle className="text-lg">Required Documents</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[
                { name: "Proof of Identity", status: "pending" },
                { name: "Proof of Address", status: "pending" },
                { name: "Last 3 Months Payslips", status: "pending" },
                { name: "Bank Statements", status: "pending" }
              ].map((doc, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-[#020617] rounded-lg">
                  <div className="flex items-center gap-3">
                    {doc.status === "uploaded" ? (
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                    ) : (
                      <Clock className="w-5 h-5 text-amber-500" />
                    )}
                    <span className="text-sm">{doc.name}</span>
                  </div>
                  <Button size="sm" variant="outline" className="border-slate-700 text-xs">
                    Upload
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
